import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { calculateAge } from "../lib/age";

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function weekdayLabel(weekday: string): string {
  switch (weekday) {
    case "Sunday":
      return m.age_wd_sunday();
    case "Monday":
      return m.age_wd_monday();
    case "Tuesday":
      return m.age_wd_tuesday();
    case "Wednesday":
      return m.age_wd_wednesday();
    case "Thursday":
      return m.age_wd_thursday();
    case "Friday":
      return m.age_wd_friday();
    default:
      return m.age_wd_saturday();
  }
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-card/40 p-4">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 font-display text-xl font-semibold tabular-nums">
        {value}
      </dd>
    </div>
  );
}

export function AgeCalculator() {
  const uid = useId();
  const [birth, setBirth] = useState("");
  const [at, setAt] = useState("");
  const locale = getLocale();

  const result = useMemo(
    () => (birth ? calculateAge(birth, at.trim() || todayISO()) : null),
    [birth, at],
  );

  const fmt = (n: number) => n.toLocaleString(locale);

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-dob`}>{m.age_dob_label()}</Label>
          <Input
            id={`${uid}-dob`}
            type="date"
            value={birth}
            onChange={(e) => setBirth(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-at`}>{m.age_at_label()}</Label>
          <Input
            id={`${uid}-at`}
            type="date"
            value={at}
            onChange={(e) => setAt(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{m.age_at_hint()}</p>
        </div>
      </div>

      <div className="mt-6" aria-live="polite">
        {birth === "" ? (
          <p className="text-sm text-muted-foreground">{m.age_enter_dob()}</p>
        ) : result === null ? (
          <p className="text-sm text-muted-foreground">{m.age_invalid()}</p>
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl bg-primary/10 p-5 text-center">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {m.age_your_age()}
              </p>
              <p
                data-testid="age-main"
                className="mt-1 font-display text-2xl font-bold text-primary sm:text-3xl"
              >
                {m.age_ymd({
                  years: fmt(result.years),
                  months: String(result.months),
                  days: String(result.days),
                })}
              </p>
            </div>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat
                label={m.age_total_months()}
                value={fmt(result.totalMonths)}
              />
              <Stat
                label={m.age_total_weeks()}
                value={fmt(result.totalWeeks)}
              />
              <Stat label={m.age_total_days()} value={fmt(result.totalDays)} />
              <Stat
                label={m.age_total_hours()}
                value={fmt(result.totalHours)}
              />
              <Stat
                label={m.age_weekday()}
                value={weekdayLabel(result.weekday)}
              />
              <Stat
                label={m.age_next_birthday()}
                value={m.age_in_days({
                  days: String(result.daysToNextBirthday),
                })}
              />
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
